<script setup>
/**
 * Making a budget. The same four questions a plan asks — what it covers, how
 * much, how often, how far off is too far — minus the one it cannot ask: which
 * day, because a budget has none.
 *
 * Unlike a plan it can cover several categories and several tags; a
 * transaction counts if it is any of them.
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { query } from '../../db'
import { fromDb } from '../../stores/live'
import { placeholders, readRow } from '../../lib/ledger-sql'
import { stepBy } from '../../lib/recurrence'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import Picker from '../ui/Picker.vue'
import { categoryOptions, fmt, ledgerStart, tagIdsOn } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { t } from '../../stores/i18n'
import { addBudget, deleteBudget, updateBudget } from '../../stores/budgets'
import { addTag } from '../../stores/tags'
import { AUTO_PERIODS_DEFAULT, BUDGET_CADENCES, buildBudgets, periodOf } from '../../lib/budgets'
import { cadenceKey } from '../../lib/recurrence'
import { currencySymbol, parseAmount } from '../../lib/money'
import { today as todayISO } from '../../lib/dates'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

const existing = computed(() =>
  props.payload?.id ? state.budgets.find((b) => b.id === props.payload.id) : null,
)

// Only the first has a word in it; the rest are the same in every language.
const TOLERANCES = [
  { value: 0, label: () => t('tolerance.exact') },
  { value: 0.02, label: () => '±2%' },
  { value: 0.05, label: () => '±5%' },
  { value: 0.1, label: () => '±10%' },
  { value: 0.25, label: () => '±25%' },
  { value: 0.5, label: () => '±50%' },
]

const form = reactive({
  type: props.payload?.type ?? 'expense',
  categories: [], // names
  tags: [], // ids
  amount: '',
  cadence: 'monthly',
  tolerance: 0.1,
  start_date: todayISO(),
  end_date: '',
  archived: false,
  auto: false,
  auto_periods: String(AUTO_PERIODS_DEFAULT),
})
const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

/** Every category in use, plus any typed in here that nothing uses yet. */
const categoryChoices = computed(() => {
  const known = new Set(categoryOptions.value.map((o) => o.name.toLowerCase()))
  const fresh = form.categories.filter((name) => !known.has(name.toLowerCase()))
  return [...fresh.map((name) => ({ id: name, name })), ...categoryOptions.value]
})
const categoryLook = (name) => (form.categories.includes(name) ? { tone: 'on' } : { tone: 'off' })
const toggleCategory = (name) => (form.categories = toggle(form.categories, name))
const addNamedCategory = (name) => {
  const clean = name.trim()
  if (clean && !form.categories.some((c) => c.toLowerCase() === clean.toLowerCase())) {
    form.categories = [...form.categories, clean]
  }
}

const tagLook = (id) => (form.tags.includes(id) ? { tone: 'on' } : { tone: 'off' })
const toggleTag = (id) => (form.tags = toggle(form.tags, id))
/** A tag named here that does not exist yet is made, like everywhere else. */
async function addNamedTag(name) {
  const tag = await addTag(name)
  if (tag && !form.tags.includes(tag.id)) form.tags = [...form.tags, tag.id]
}

const error = ref('')
const confirmDelete = ref(false)

onMounted(() => {
  const b = existing.value
  if (b)
    Object.assign(form, {
      ...b,
      categories: (b.categoryIds ?? [])
        .map((id) => state.categories.find((c) => c.id === id)?.name)
        .filter(Boolean),
      tags: (b.tagIds ?? []).filter((id) => state.tags.some((tag) => tag.id === id)),
      amount: b.amount ? String(b.amount) : '',
      end_date: b.end_date ?? '',
      archived: !!b.archived,
      auto: !!b.auto,
      auto_periods: String(b.auto_periods ?? AUTO_PERIODS_DEFAULT),
    })
})

const symbol = computed(() => currencySymbol(state.settings.currency))
const period = computed(() => periodOf(form, todayISO()))
/** N, when the field holds a usable one. */
const autoPeriods = computed(() => {
  const n = Number(String(form.auto_periods).trim())
  return Number.isInteger(n) && n >= 1 && n <= 120 ? n : null
})

/**
 * What an automatic budget would expect of the period running today, worked
 * out exactly as the panel will — so the number here is the number there.
 */
/** The rows the draft would count over the periods it averages — asked of the database as it changes. */
const previewRows = fromDb(
  () => {
    if (!form.auto || !autoPeriods.value || !period.value) return null
    if (!form.categories.length && !form.tags.length) return null
    return {
      categories: [...form.categories],
      tags: [...form.tags],
      from: stepBy(form.cadence, period.value.from, -autoPeriods.value),
      to: period.value.from,
    }
  },
  async ({ categories, tags, from, to }) => {
    const rows = await query(
      `SELECT t.id, t.date, t.amount, t.description, COALESCE(c.name, '') AS category,
              (SELECT group_concat(tag_id) FROM transaction_tags WHERE transaction_id = t.id) AS own_tags
         FROM transactions t LEFT JOIN categories c ON c.id = t.category_id
        WHERE t.date BETWEEN ? AND ?
          AND (c.name IN (${placeholders(categories)})
               OR EXISTS (SELECT 1 FROM transaction_tags x WHERE x.transaction_id = t.id AND x.tag_id IN (${placeholders(tags)}))
               OR EXISTS (SELECT 1 FROM category_tags x WHERE x.category_id = t.category_id AND x.tag_id IN (${placeholders(tags)})))`,
      [from, to, ...categories, ...tags, ...tags],
    )
    return rows.map(readRow)
  },
  [],
)

const autoPreview = computed(() => {
  if (!form.auto || !autoPeriods.value || !period.value) return null
  if (!form.categories.length && !form.tags.length) return null
  const [row] = buildBudgets(
    [{ ...form, id: 'preview', amount: 0, archived: 0, auto: 1, auto_periods: autoPeriods.value }],
    previewRows.value,
    { from: period.value.from, to: period.value.from, today: todayISO(), tagsOf: tagIdsOn, since: ledgerStart.value },
  )
  return row ? { amount: row.expected, basis: row.autoBasis } : null
})

const room = computed(() => {
  const amount = form.auto ? (autoPreview.value?.amount ?? NaN) : parseAmount(form.amount)
  return Number.isFinite(amount) ? Math.abs(amount) * form.tolerance : null
})

async function save() {
  const amount = parseAmount(form.amount)
  if (!form.categories.length && !form.tags.length) return (error.value = t('budgetSheet.needCategory'))
  if (form.auto) {
    if (!autoPeriods.value) return (error.value = t('budgetSheet.needPeriods'))
  } else if (!Number.isFinite(amount) || amount === 0) {
    return (error.value = t('budgetSheet.needAmount'))
  }
  const payload = {
    ...form,
    amount: Number.isFinite(amount) ? amount : 0,
    auto_periods: autoPeriods.value ?? AUTO_PERIODS_DEFAULT,
    end_date: form.end_date || null,
  }
  if (existing.value) await updateBudget(existing.value.id, payload)
  else {
    count('budget.add')
    await addBudget(payload)
  }
  toast(t(existing.value ? 'budgetSheet.updated' : 'budgetSheet.added'), 'good')
  closeSheet()
}

async function destroy() {
  if (!confirmDelete.value) return (confirmDelete.value = true)
  await deleteBudget(existing.value.id)
  toast(t('budgetSheet.deleted'))
  closeSheet()
}
</script>

<template>
  <Sheet
    :title="t(existing ? 'budgetSheet.edit' : 'budgets.add')"
    :subtitle="t('budgetSheet.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <div class="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
        <button
          v-for="t in [
            { value: 'expense', label: t('budgetSheet.limit') },
            { value: 'income', label: t('budgetSheet.target') },
          ]"
          :key="t.value"
          class="rounded-lg py-2 text-[13.5px] font-semibold transition"
          :class="form.type === t.value ? 'bg-surface text-text shadow-sm' : 'text-muted'"
          @click="form.type = t.value"
        >
          {{ t.label }}
        </button>
      </div>

      <div>
        <label class="flex items-center gap-2.5 text-[13.5px]">
          <input v-model="form.auto" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
          {{ t('budgetSheet.auto') }}
        </label>
      </div>

      <div v-if="!form.auto">
        <label class="label" for="bg-amount">{{ t('common.amount') }}</label>
        <div class="relative">
          <span class="absolute top-1/2 left-3 -translate-y-1/2 text-faint">{{ symbol }}</span>
          <input
            id="bg-amount"
            v-model="form.amount"
            class="input tnum pl-7"
            inputmode="decimal"
            placeholder="0.00"
          />
        </div>
      </div>

      <div v-else>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="label" for="bg-auto-periods">{{ t('budgetSheet.autoPeriods') }}</label>
            <input
              id="bg-auto-periods"
              v-model="form.auto_periods"
              type="number"
              inputmode="numeric"
              min="1"
              max="120"
              step="1"
              class="input tnum"
              :class="!autoPeriods && '!border-danger'"
              :aria-invalid="!autoPeriods"
            />
          </div>
          <!-- What those periods average to, beside the number that decides it,
               so changing one shows the other move. -->
          <div>
            <label class="label" for="bg-auto-average">{{ t('budgetSheet.autoAverage') }}</label>
            <output
              id="bg-auto-average"
              for="bg-auto-periods"
              class="input tnum block truncate bg-raised/50 font-semibold"
              :class="!autoPreview?.basis && 'font-normal text-faint'"
              aria-live="polite"
            >
              {{ autoPreview?.basis ? fmt(autoPreview.amount) : '—' }}
            </output>
          </div>
        </div>
        <p
          v-if="!form.categories.length && !form.tags.length"
          class="mt-1.5 text-[12.5px] text-muted"
        >
          {{ t('budgetSheet.autoPickFirst') }}
        </p>
        <p v-else-if="autoPreview && !autoPreview.basis" class="mt-1.5 text-[12.5px] text-muted">
          {{ t('budgetSheet.autoNoHistory') }}
        </p>
        <p
          v-else-if="autoPreview && autoPeriods && autoPreview.basis < autoPeriods"
          class="mt-1.5 text-[12.5px] text-muted"
        >
          {{ t('budgetSheet.autoShort', { count: autoPreview.basis }) }}
        </p>
      </div>

      <div>
        <label class="label" for="bg-cat">{{ t('budgetSheet.categories') }}</label>
        <Picker
          id="bg-cat"
          :create-label="t('common.use')"
          :placeholder="t('budgetSheet.categoryPlaceholder')"
          :options="categoryChoices"
          :look="categoryLook"
          @pick="toggleCategory"
          @create="addNamedCategory"
        />
      </div>

      <div>
        <label class="label" for="bg-tag">{{ t('charts.tags') }}</label>
        <Picker
          id="bg-tag"
          :options="state.tags"
          :look="tagLook"
          :placeholder="t('common.searchTags')"
          @pick="toggleTag"
          @create="addNamedTag"
        />
        <p class="mt-1.5 text-[12px] text-faint">{{ t('budgetSheet.coversHint') }}</p>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="bg-cadence">{{ t('budgetSheet.every') }}</label>
          <select id="bg-cadence" v-model="form.cadence" class="input">
            <option v-for="c in BUDGET_CADENCES" :key="c.value" :value="c.value">
              {{ t(cadenceKey(c.value)) }}
            </option>
          </select>
        </div>
        <div>
          <label class="label" for="bg-start">{{ t('budgetSheet.inEffect') }}</label>
          <input id="bg-start" v-model="form.start_date" type="date" class="input" />
        </div>
      </div>

      <p v-if="period" class="tnum -mt-1 text-[12.5px] text-muted">
        {{ t('budgetSheet.periodRuns', { from: period.from, to: period.to }) }}
      </p>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="bg-end">
            {{ t('common.endsOn') }} <span class="normal-case">{{ t('common.optional') }}</span>
          </label>
          <input id="bg-end" v-model="form.end_date" type="date" class="input" />
        </div>
      </div>

      <div class="rounded-xl border border-line-soft bg-raised/50 p-3.5">
        <p class="flex items-center gap-2 text-[12.5px] font-semibold text-muted">
          <Icon name="link" :size="15" /> {{ t('budgetSheet.howItWorks') }}
        </p>
        <p class="mt-1.5 mb-3 text-[12px] text-faint">
          {{
            t('budgetSheet.howItWorksDetail', {
              direction: t(form.type === 'income' ? 'budgetSheet.comingIn' : 'budgetSheet.goingOut'),
            })
          }}
        </p>
        <div class="mt-3">
          <label class="label" for="bg-tol">
            {{ t(form.type === 'income' ? 'budgetSheet.warnShort' : 'budgetSheet.warnOver') }}
          </label>
          <select id="bg-tol" v-model.number="form.tolerance" class="input">
            <option v-for="tol in TOLERANCES" :key="tol.value" :value="tol.value">
              {{ tol.label() }}
            </option>
          </select>
          <p v-if="room !== null" class="tnum mt-1.5 text-[12px] text-faint">
            {{
              form.tolerance === 0
                ? t('budgetSheet.exactNote')
                : t('budgetSheet.roomNote', { amount: fmt(room) })
            }}
          </p>
        </div>
      </div>

      <label v-if="existing" class="flex items-center gap-2.5 text-[13.5px]">
        <input v-model="form.archived" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
        {{ t('budgetSheet.archive') }}
      </label>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>
    </div>

    <template #footer>
      <button v-if="existing" class="btn-danger" @click="destroy">
        <Icon name="trash" :size="16" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <div class="flex-1" />
      <button class="btn-soft" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="save"><Icon name="check" :size="16" /> {{ t('common.save') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
